// La fiche d'un partenaire (bureau), #/partenaires/<id>, d'après la maquette g1 (D-125) : en tête, son niveau, son nom,
// depuis quand et son contact ; puis « Garder le lien » (dernier geste, l'attention proposée, le fil) ; puis la fiche
// elle-même (contrat, visibilité, comptes, espace) dans la feuille « Modifier la fiche » de D-033, inchangée.
// Les dons et reçus fiscaux (D-124) ont leur section dans #pf-more ; D-147 : la promesse de don de l'app offerte y est en
// tête (« don promis », « Noter le règlement reçu »).
import { partnersAdmin, partnerStats } from '../lib/api';
import { KIND_LABEL } from '../domain/partners';
import { seasonOf } from '../domain/season';
import { localDate } from '../domain/schedule';
import type { Ctx } from './context';
import { $, html, mount } from './dom';
import { openPartnerEditor, offerLine, partnerMark } from './partners';
import { renderLienSection } from './lien';
import { openGiftSheet, partnerGiftsBlock } from './recus';
import { entryOn } from './recus-entries';
import { officialOfferAdmin } from '../lib/lien-api';
import { ddmmyyyy, eur } from '../domain/recus';
import { openPledgePaid } from './official-partner';

export async function renderPartnerPage(root: HTMLElement, ctx: Ctx, clubId: string, partnerId: string): Promise<void> {
  const list = await partnersAdmin(clubId).catch(() => []);
  const p = list.find(x => x.id === partnerId);
  if (!p) {
    mount(root, html`<section class="screen"><a class="back" href="#/partenaires">‹ Partenaires</a>
      <div class="empty"><p class="empty-title">Partenaire introuvable.</p><p>Il a peut-être été retiré.</p></div></section>`);
    return;
  }
  const again = () => renderPartnerPage(root, ctx, clubId, partnerId);
  mount(root, html`
    <section class="screen">
      <a class="back" href="#/partenaires">‹ Partenaires</a>
      <header class="screen-head"><p class="eyebrow">Partenaire${p.level ? ` · ${p.level}` : ''}</p><h2>${p.name}</h2>
        ${p.since_year || p.contact_name ? html`<p class="muted">${[p.since_year ? `Depuis ${p.since_year}` : '', p.contact_name ?? ''].filter(Boolean).join(' · ')}</p>` : ''}</header>
      <div id="pf-lien"></div>
      <div id="pf-more"></div>
      <section class="block pf-card"><div class="block-head"><h3>La fiche</h3></div>
        <div class="pf-id">${partnerMark(p, 'pt-logo')}
          <span class="card-main"><strong>${KIND_LABEL[p.kind]}</strong>
            ${p.member_offer && !p.restricted ? html`<span class="muted small">Offre : ${offerLine(p.member_offer, p.member_offer_frequency)}</span>` : ''}
            ${p.restricted ? html`<span class="chip chip-ko">Jamais affiché (loi Évin)</span>` : ''}</span></div>
        <div class="btn-row"><button type="button" class="btn btn-ghost btn-sm" id="pf-edit">Modifier la fiche</button>
          <a class="btn btn-ghost btn-sm" href="/p/${p.report_token}" target="_blank" rel="noopener">Son espace partenaire ›</a></div>
      </section>
    </section>`);
  void renderLienSection($(root, '#pf-lien')!, ctx, p.id);
  // Dons et reçus fiscaux (D-124, module partenaires) : la liste, « Noter un don ou un sponsoring ».
  if (entryOn(ctx, 'dons')) {
    const more = $(root, '#pf-more')!;
    mount(more, html`<section class="block pf-card" data-guide="pf-gifts"><div class="block-head"><h3>Dons et reçus fiscaux</h3></div>
      <div id="pf-gifts"></div>
      <button type="button" class="btn btn-ghost btn-sm" id="pt-gift" data-guide="pt-gift">Noter un don ou un sponsoring</button>
      <p class="hint">Un don donne un reçu fiscal (numéroté au registre) ; un sponsoring se facture, sans reçu. L’argent ne passe pas par l’app.</p></section>`);
    const offer = await officialOfferAdmin(clubId).catch(() => null);
    const o = offer?.offer;
    const pledged = o && o.partner_id === p.id && o.pledge && o.status === 'confirme' && !o.paid_at ? o : null;
    void partnerGiftsBlock($(more, '#pf-gifts')!, p.id, pledged && offer?.season ? html`<ul class="rx-trips"><li><span>${ddmmyyyy(localDate(new Date(pledged.pledged_at ?? Date.now())))} · don promis<br>
      <span class="muted small">L’app offerte pour la saison ${offer.season.name} · reçu fiscal au règlement</span></span><b>${eur(pledged.amount_cents)}</b></li></ul>
      <div class="btn-row"><button type="button" class="btn btn-primary btn-sm" id="pf-paid" data-guide="pf-paid">Noter le règlement reçu</button></div>` : null)
      .then(() => { if (offer) $(more, '#pf-paid')?.addEventListener('click', () => openPledgePaid(ctx, offer, list, () => void again())); });
    $(more, '#pt-gift')!.addEventListener('click', () =>
      void openGiftSheet(ctx, { id: p.id, name: p.name, contact_email: p.contact_email ?? null, contact_name: p.contact_name ?? null }, () => void again()));
  }
  $(root, '#pf-edit')!.addEventListener('click', async () => {
    const stats = await partnerStats([p.id], seasonOf(localDate(new Date())).from).catch(() => []);
    openPartnerEditor(ctx, clubId, p, stats, () => void again());
  });
}
